import React, { useRef, useState } from "react";

import AppPlaceholderPhoto from "@common/assets/images/shared-icons/app-icon-placeholder.svg";
import ChromeAppPlaceholderPhoto from "@common/assets/images/shared-icons/purple-app-icon-placeholder.svg";
import { NavItem } from "@common/components/Navigation/Navigation";
import Paragraph from "@common/components/Paragraph/Paragraph";
import Tooltip from "@common/components/Tooltip/Tooltip";
import useOnClickOutside from "@common/hooks/useOnClickOutside";
import { App } from "@common/models";
import { vars } from "@common/styles";
import { Flex } from "@common/styles/reusable/Flex/Flex.styles";
import { getOSImage } from "@common/util/contentResolvers";
import identifiers from "@common/util/identifiers.json";
import useWorkspaceAndAppChange from "@main/consumers/useWorkspaceAndAppChange";
import { useUnreadNotificationsContext } from "@main/context/App/UnreadNotificationsContext";

import AppListDropdown from "./AppListDropdown";
import * as Styled from "./AppSwitcher.styles";

interface AppSwitcherProps {
  selectedApp: App;
  appList: App[];
}

export default function AppSwitcher({ selectedApp, appList }: AppSwitcherProps) {
  const { handleAppChange } = useWorkspaceAndAppChange();
  const { unreadNotifications } = useUnreadNotificationsContext();

  const [isDropdownMenuOpen, setIsDropdownMenuOpen] = useState(false);

  const optionsRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(optionsRef, () => isDropdownMenuOpen && setIsDropdownMenuOpen(false));

  const selectedAppName = appList.find((app) => app.id === selectedApp.id)?.name;
  const filteredAppList = appList.filter((app) => app.id !== selectedApp.id);

  const hasNotificationsOnOtherApps = Object.keys(unreadNotifications)
    .filter((appId) => appId !== selectedApp.id) // Exclude the selected app
    .some((appId) => unreadNotifications[appId]?.length > 0); // Check if any other app has notifications

  return (
    <NavItem onClick={() => setIsDropdownMenuOpen(!isDropdownMenuOpen)}>
      <Styled.SelectedAppWithOptions ref={optionsRef}>
        <Tooltip
          position="bottom"
          text={"Switch apps and more..."}
          offset={1.5}
        >
          <Flex>
            <Styled.SelectedAppContainer data-testid={identifiers["navbar.selectedApp.button"]}>
              <Flex
                $alignItems="center"
                style={{ paddingLeft: "0.8rem" }}
              >
                <img
                  src={
                    selectedApp.logo_url
                      ? selectedApp.logo_url
                      : selectedApp.is_extension
                      ? ChromeAppPlaceholderPhoto
                      : AppPlaceholderPhoto
                  }
                  width={28}
                  height={28}
                  style={{ borderRadius: "0.8rem", objectFit: "cover" }}
                />
                <Styled.SelectedApp>
                  <Styled.AppNameParagraph>{selectedAppName}</Styled.AppNameParagraph>
                  <img
                    src={getOSImage(selectedApp.platform)}
                    width={20}
                    height={20}
                  />
                </Styled.SelectedApp>
              </Flex>
            </Styled.SelectedAppContainer>
            <Styled.IconButton
              $active={isDropdownMenuOpen}
              data-testid={identifiers["navbar.selectedApp.options.button"]}
            >
              <Styled.Down />
              {hasNotificationsOnOtherApps && (
                <Styled.OptionsNotificationIndicator>
                  <Paragraph
                    color={vars.colors.white}
                    fontSize={12}
                  >
                    <Styled.SmallDot />
                  </Paragraph>
                </Styled.OptionsNotificationIndicator>
              )}
            </Styled.IconButton>
          </Flex>
        </Tooltip>

        <AppListDropdown
          appList={filteredAppList}
          onAppSwitch={handleAppChange}
          displayNotifications={true}
          isDropdownMenuOpen={isDropdownMenuOpen}
          setIsDropdownMenuOpen={setIsDropdownMenuOpen}
        />
      </Styled.SelectedAppWithOptions>
    </NavItem>
  );
}
